(() => {
  'use strict';

  const root = document.querySelector('[data-ordering-dog]');

  if (!root) return;

  /*
   * ============================================================
   * CONFIGURATION
   * ============================================================
   */

  /*
   * PRICING RULES (Phase 2, section 2)
   *
   * Everything is calculated from two numbers:
   * ounces a day and days a week.
   *
   * Shopify setup:
   *   Each Daily Amount variant (4 to 400 oz) is priced PER DAY.
   *   The cart quantity is the number of days a week (1 to 7).
   *   Shopify line price = daily price x days = weekly price.
   */

  const PRICE_PER_UNIT = 5.00;        // $5.00 ...
  const OUNCES_PER_PRICE_UNIT = 4;    // ... per 4 oz a day

  const MIN_DAYS_PER_WEEK = 1;
  const MAX_DAYS_PER_WEEK = 7;
  const DEFAULT_DAYS_PER_WEEK = 7;

  const MEALS_PER_DAY = 2;            // "if you feed twice a day"

  const GRAMS_PER_OUNCE = 28.35;

  const FREE_DELIVERY_THRESHOLD = 100.00; // whole order, per week
  const DELIVERY_FEE = 10.00;

  const MAX_CONTAINER_OUNCES = 36;

  /*
   * DELIVERY ROUTES (Phase 2, section 3)
   *
   * daysAfterCutoff: the delivery day counted from the
   * Wednesday 11:59pm Pacific cutoff.
   *   Wed cutoff -> Mon (+5), Tue (+6), Thu (+8)
   *
   * ZIP codes are NOT kept here. They come from the product
   * metafield custom.delivery_zip_codes (see the liquid file),
   * so they can be kept in step with the Shopify Local
   * Delivery zones without editing code.
   */
  const ROUTES = {
    salem: {
      key: 'salem',
      area: 'Salem',
      dayName: 'Monday',
      daysAfterCutoff: 5,
      banner:
        'Fresh dog food, cooked in Salem and delivered every Monday.'
    },
    portland: {
      key: 'portland',
      area: 'Portland',
      dayName: 'Tuesday',
      daysAfterCutoff: 6,
      banner:
        'Fresh dog food, cooked in Salem and delivered every Tuesday in Portland.'
    },
    newberg: {
      key: 'newberg',
      area: 'Wilsonville and Newberg',
      dayName: 'Thursday',
      daysAfterCutoff: 8,
      banner:
        'Fresh dog food, cooked in Salem and delivered every Thursday in Wilsonville and Newberg.'
    }
  };

  const ZIP_STORAGE_KEY = 'pp_delivery_zip';

  const MIN_OUNCES = 4;
  const MAX_OUNCES = 400;

  const AMOUNT_STEP = 2;
  const DEFAULT_AMOUNT = 12;

  const RECIPE_NAME =
    root.dataset.recipeName || 'Harvest Turkey Recipe';

  const TOPPER_OPTIONS = [4, 8, 12];

  /*
   * Feeding lookup is primarily loaded from the product metafield.
   *
   * Fallback is included so the flow still works if the metafield
   * is temporarily empty.
   */

  const FALLBACK_LOOKUP = {
    'Under 10 lbs': {
      Adult: {
        Indoor: 6,
        'Less Active': 8,
        Active: 10
      }
    },

    '10-20 lbs': {
      Adult: {
        Indoor: 10,
        'Less Active': 12,
        Active: 16
      }
    },

    '20-40 lbs': {
      Adult: {
        Indoor: 18,
        'Less Active': 20,
        Active: 26
      }
    },

    '40-70 lbs': {
      Adult: {
        Indoor: 28,
        'Less Active': 32,
        Active: 42
      }
    },

    '70-100 lbs': {
      Adult: {
        Indoor: 38,
        'Less Active': 46,
        Active: 58
      }
    },

    'Over 100 lbs': {
      Adult: {
        Indoor: 44,
        'Less Active': 52,
        Active: 66
      }
    }
  };


  /*
   * ============================================================
   * ELEMENT HELPERS
   * ============================================================
   */

  const $ = (selector, scope = root) =>
    scope.querySelector(selector);

  const $$ = (selector, scope = root) =>
    Array.from(scope.querySelectorAll(selector));


  /*
   * ============================================================
   * PARSE JSON DATA
   * ============================================================
   */

  function parseJSONScript(selector, fallback) {
    const element = $(selector);

    if (!element) {
      return fallback;
    }

    try {
      return JSON.parse(element.textContent || '');
    } catch (error) {
      console.error(
        `[ordering-dog] Could not parse ${selector}`,
        error
      );

      return fallback;
    }
  }

  const metafields = parseJSONScript(
    '[data-ordering-metafields]',
    {}
  );

  const variants = parseJSONScript(
    '[data-ordering-variants]',
    []
  );

  const sellingPlans = parseJSONScript(
    '[data-ordering-selling-plans]',
    []
  );

  /*
   * { "salem": ["97301", ...], "portland": [...], "newberg": [...] }
   */
  const deliveryZones = parseJSONScript(
    '[data-ordering-delivery-zones]',
    {}
  );


  /*
   * ============================================================
   * NORMALIZATION
   * ============================================================
   */

  function normalize(value) {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[–—]/g, '-')
      .replace(/\s+/g, ' ');
  }

  function normalizeArray(value) {
    if (Array.isArray(value)) {
      return value;
    }

    if (
      value &&
      typeof value === 'object' &&
      Array.isArray(value.values)
    ) {
      return value.values;
    }

    if (typeof value === 'string') {
      return value
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);
    }

    return [];
  }


  /*
   * ============================================================
   * DOG NAME
   * ============================================================
   */

  /*
   * 4.2 The customer's own name for the dog, or '' when none.
   * Never "Dog 1": that label is only used on the packing slip.
   */
  function getDogName() {
    const name = String(state.dogName || '').trim();

    return name
      ? name.charAt(0).toLocaleUpperCase() + name.slice(1)
      : '';
  }

  /*
   * "Bo" / "your dog"
   */
  function getDogLabel(name = getDogName()) {
    return name || 'your dog';
  }

  /*
   * 4.3 "Bo's", "Gus'" (names ending in s), or "your dog's".
   */
  function getDogPossessive(name = getDogName()) {
    if (!name) return "your dog's";

    return /s$/i.test(name) ? `${name}'` : `${name}'s`;
  }

  function updateDogNameOutputs() {
    const label = getDogLabel();
    const possessive = getDogPossessive();

    $$('[data-dog-name-output]').forEach(element => {
      element.textContent = label;
    });

    $$('[data-result-dog-name]').forEach(element => {
      element.textContent = label;
    });

    $$('[data-dog-name-possessive]').forEach(element => {
      element.textContent = possessive;
    });
  }


  /*
   * ============================================================
   * VARIANT LOOKUP
   * ============================================================
   *
   * IMPORTANT:
   * No variant IDs are hard-coded.
   *
   * We detect the Daily Amount option dynamically.
   */

  function parseDailyAmount(value) {
    if (value === null || value === undefined) {
      return null;
    }

    let text = String(value)
      .trim()
      .toLowerCase();

    text = text
      .replace(/oz\/day/g, '')
      .replace(/oz\s*\/\s*day/g, '')
      .replace(/oz\s*a\s*day/g, '')
      .replace(/ounces?\s*(per|a)\s*day/g, '')
      .replace(/ounces?/g, '')
      .trim();

    const match = text.match(/-?\d+(?:\.\d+)?/);

    if (!match) {
      return null;
    }

    const amount = Number(match[0]);

    if (!Number.isFinite(amount)) {
      return null;
    }

    return amount;
  }

  function getOptionName(option) {
    if (!option) {
      return '';
    }

    if (typeof option === 'string') {
      return option;
    }

    return option.name || '';
  }

  function getOptionValue(option) {
    if (!option) {
      return '';
    }

    if (typeof option === 'string') {
      return option;
    }

    return option.value || '';
  }

  function getVariantDailyAmount(variant) {
    /*
    * Preferred:
    * optionsWithValues = [
    *   { name: "Daily Amount", value: "32 oz/day" }
    * ]
    */

    if (Array.isArray(variant.optionsWithValues)) {
      for (const option of variant.optionsWithValues) {
        const optionName =
          normalize(getOptionName(option));

        if (
          optionName === 'daily amount' ||
          optionName.includes('daily amount')
        ) {
          const amount =
            parseDailyAmount(
              getOptionValue(option)
            );

          if (
            amount !== null &&
            amount >= MIN_OUNCES &&
            amount <= MAX_OUNCES &&
            amount % AMOUNT_STEP === 0
          ) {
            return amount;
          }

          return null;
        }
      }
    }

    /*
    * Fallback:
    * inspect raw options.
    */

    if (Array.isArray(variant.options)) {
      for (const option of variant.options) {
        const amount =
          parseDailyAmount(option);

        if (
          amount !== null &&
          amount >= MIN_OUNCES &&
          amount <= MAX_OUNCES &&
          amount % AMOUNT_STEP === 0
        ) {
          return amount;
        }
      }
    }

    return null;
  }

  const variantByDailyAmount = new Map();

  variants.forEach(variant => {
    const amount = getVariantDailyAmount(variant);

    if (amount === null) {
      return;
    }

    variantByDailyAmount.set(
      Number(amount),
      {
        id: Number(variant.id),
        available: Boolean(variant.available),
        price: Number(variant.price || 0)
      }
    );
  });


  /*
   * ============================================================
   * SELLING PLAN LOOKUP
   * ============================================================
   *
   * We select a recurring weekly plan dynamically.
   */

  function findWeeklySellingPlan() {
    if (!Array.isArray(sellingPlans)) {
      return null;
    }

    const recurringPlans = sellingPlans.filter(plan => {
      return (
        plan &&
        plan.recurringDeliveries !== false
      );
    });

    const weekly = recurringPlans.find(plan => {
      const text = normalize([
        plan.name,
        plan.groupName,
        plan.description,
        ...(Array.isArray(plan.options)
          ? plan.options.map(option => {
              if (!option) return '';

              if (typeof option === 'string') {
                return option;
              }

              return [
                option.name,
                option.value
              ].filter(Boolean).join(' ');
            })
          : [])
      ].join(' '));

      return (
        text.includes('week') ||
        text.includes('weekly')
      );
    });

    return weekly || null;
  }

  const weeklySellingPlan = findWeeklySellingPlan();


  /*
   * ============================================================
   * STATE
   * ============================================================
   */

  const state = {
    dogName: '',
    feed: '',
    weight: '',
    age: '',
    activity: '',

    ounces: DEFAULT_AMOUNT,

    /*
     * Days of food each week (1 to 7).
     * Sent to the cart as the line item quantity.
     */
    daysPerWeek: DEFAULT_DAYS_PER_WEEK,

    largeAmountConfirmed: false,

    currentStep: 'feed',

    /*
     * Delivery ZIP and its route, from the ZIP check.
     */
    zip: '',
    route: null,

    /*
     * True when the customer entered their own daily amount
     * (changes the step count).
     */
    knownAmountPath: false,

    /*
     * True when the amount came from the customer (not the
     * recommendation). Changes the result card heading.
     */
    customAmount: false,

    stepHistory: [],

    /*
     * Saved dogs.
     */
    dogs: [],

    /*
     * When editing a dog this is its index.
     * null means we are creating a new dog.
     */
    editingDogIndex: null,

    /*
     * Dynamic variant.
     */
    variantId: null,
    variantPrice: 0,

    /*
     * Used for preventing accidental duplicate checkout clicks.
     */
    checkoutInProgress: false
  };


  /*
   * ============================================================
   * PRICING
   * ============================================================
   */

  function roundMoney(value) {
    return Math.round(Number(value || 0) * 100) / 100;
  }

  function clampDays(value) {
    const days = Math.round(Number(value));

    if (!Number.isFinite(days)) {
      return DEFAULT_DAYS_PER_WEEK;
    }

    return Math.max(
      MIN_DAYS_PER_WEEK,
      Math.min(MAX_DAYS_PER_WEEK, days)
    );
  }

  /*
   * Price per day = oz a day / 4 x $5.00
   */
  function calculateDailyPrice(ounces) {
    const amount = Number(ounces);

    if (!Number.isFinite(amount)) {
      return 0;
    }

    return roundMoney(
      amount / OUNCES_PER_PRICE_UNIT * PRICE_PER_UNIT
    );
  }

  /*
   * The Shopify variant price is the source of truth
   * (it is what the customer is charged).
   * The formula is only a fallback for the UI.
   */
  function getVariantPriceDaily(ounces) {
    const variant = variantByDailyAmount.get(
      Number(ounces)
    );

    if (!variant) {
      return null;
    }

    /*
     * Shopify variant prices are stored in cents.
     */
    return variant.price / 100;
  }

  function getDailyPrice(ounces) {
    const variantPrice = getVariantPriceDaily(ounces);

    if (variantPrice !== null) {
      return variantPrice;
    }

    return calculateDailyPrice(ounces);
  }

  /*
   * Price per week = price per day x days a week
   */
  function getWeeklyPrice(
    ounces,
    days = state.daysPerWeek
  ) {
    return roundMoney(
      getDailyPrice(ounces) * clampDays(days)
    );
  }


  /*
   * ============================================================
   * CONTAINERS, MEALS
   * ============================================================
   */

  /*
   * Containers per day = oz a day / 36, rounded up
   * (1 container up to 36 oz, 2 for 37 to 72 oz)
   */
  function calculateContainersPerDay(ounces) {
    return Math.max(
      1,
      Math.ceil(Number(ounces) / MAX_CONTAINER_OUNCES)
    );
  }

  /*
   * Containers per week = containers per day x days a week
   */
  function calculateContainersPerWeek(
    ounces,
    days = state.daysPerWeek
  ) {
    return (
      calculateContainersPerDay(ounces) *
      clampDays(days)
    );
  }

  /*
   * Meals = days a week x 2
   */
  function calculateMeals(days = state.daysPerWeek) {
    return clampDays(days) * MEALS_PER_DAY;
  }

  /*
   * One place for every number shown for a plan.
   */
  function calculatePlan(
    ounces,
    days = state.daysPerWeek
  ) {
    const daysPerWeek = clampDays(days);
    const dailyPrice = getDailyPrice(ounces);

    return {
      ounces: Number(ounces),
      grams: ouncesToGrams(ounces),
      daysPerWeek,
      dailyPrice,
      weeklyPrice: roundMoney(dailyPrice * daysPerWeek),
      containersPerDay: calculateContainersPerDay(ounces),
      containersPerWeek:
        calculateContainersPerDay(ounces) * daysPerWeek,
      meals: daysPerWeek * MEALS_PER_DAY
    };
  }

  /*
   * Delivery = free when the whole order is $100.00 or more
   * a week, otherwise $10.00.
   */
  function calculateOrderTotals(dogs = state.dogs) {
    const subtotal = roundMoney(
      dogs.reduce(
        (sum, dog) => sum + Number(dog.weeklyPrice || 0),
        0
      )
    );

    const freeDelivery =
      subtotal >= FREE_DELIVERY_THRESHOLD;

    const deliveryFee =
      subtotal > 0 && !freeDelivery ? DELIVERY_FEE : 0;

    return {
      subtotal,
      deliveryFee,
      freeDelivery,
      amountToFreeDelivery: freeDelivery
        ? 0
        : roundMoney(FREE_DELIVERY_THRESHOLD - subtotal),
      total: roundMoney(subtotal + deliveryFee)
    };
  }

  function pluralize(count, singular, plural = `${singular}s`) {
    return `${count} ${count === 1 ? singular : plural}`;
  }

  /*
   * "7 days of food · 14 meals · 7 containers"
   */
  function formatPlanAmount(plan, options = {}) {
    const { mealsSuffix = '' } = options;

    return [
      `${pluralize(plan.daysPerWeek, 'day')} of food`,
      `${pluralize(plan.meals, 'meal')}${mealsSuffix}`,
      pluralize(plan.containersPerWeek, 'container')
    ].join(' · ');
  }

  /*
   * Developer check: warns if a variant still carries the old
   * weekly price instead of the daily price.
   */
  function checkVariantPrices() {
    const mismatched = [];

    variantByDailyAmount.forEach((variant, ounces) => {
      const expected = calculateDailyPrice(ounces);
      const actual = variant.price / 100;

      if (Math.abs(expected - actual) > 0.005) {
        mismatched.push({ ounces, expected, actual });
      }
    });

    if (mismatched.length) {
      console.warn(
        `[ordering-dog] ${mismatched.length} Daily Amount ` +
        'variant(s) are not priced per day (oz / 4 x $5.00):',
        mismatched
      );
    }
  }


  /*
   * ============================================================
   * FORMATTING
   * ============================================================
   */

  function formatMoney(value) {
    const number = Number(value || 0);

    return number.toLocaleString(
      undefined,
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    );
  }
  function ouncesToGrams(ounces) {
    return Math.round((Number(ounces) * GRAMS_PER_OUNCE) / 10) * 10;
  }
  function formatDate(date) {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    }).format(date);
  }


  /*
   * ============================================================
   * PACIFIC CUTOFF
   * ============================================================
   *
   * The requirement is always the NEXT Wednesday.
   *
   * Example:
   * Wednesday Oct 7 -> cutoff Wednesday Oct 14.
   */

  /* Use Pacific time for the weekly order cutoff. */
  function getPacificParts(date = new Date()) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    const result = {};

    formatter.formatToParts(date).forEach(part => {
      if (part.type !== 'literal') {
        result[part.type] = part.value;
      }
    });

    return result;
  }

  function getNextWednesday() {
    const pacific = getPacificParts(new Date());

    const weekdayMap = {
      Sun: 0,
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6
    };

    // Wednesday is still the cutoff day until 11:59pm Pacific.
    const daysUntilWednesday =
      (3 - weekdayMap[pacific.weekday] + 7) % 7;

    return new Date(Date.UTC(
      Number(pacific.year),
      Number(pacific.month) - 1,
      Number(pacific.day) + daysUntilWednesday,
      12
    ));
  }
  function formatDeliveryWeek(date) {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric'
    }).format(date);
  }
  function getFirstDeliveryWeek(cutoff = getNextWednesday()) {
    const firstDelivery = new Date(cutoff);
    firstDelivery.setUTCDate(firstDelivery.getUTCDate() + 5);
    return firstDelivery;
  }



  /*
   * 6.3 This week's cutoff (Wednesday 11:59pm Pacific) has passed
   * when today is Thursday to Sunday.
   */
  function hasThisWeeksCutoffPassed() {
    const weekday = getPacificParts(new Date()).weekday;
    return ['Thu', 'Fri', 'Sat', 'Sun'].includes(weekday);
  }

  /*
   * 6.2 "Delivered Tuesdays. First delivery: Tuesday, Nov 3."
   * 6.3 "That's 12 days from today because this week's order
   *      cutoff (Wednesday 11:59pm) has passed."
   */
  function updateDeliveryDates() {
    const list = $('[data-dogs-list]');
    const summaryStep = list &&
      list.closest('[data-step="summary"]');

    if (!summaryStep) return;

    let deliveryDetails = summaryStep.querySelector(
      '[data-ordering-delivery-copy]'
    );

    const info = getDeliveryInfo();

    if (!info) {
      if (deliveryDetails) deliveryDetails.hidden = true;
      return;
    }

    if (!deliveryDetails) {
      deliveryDetails = document.createElement('div');
      deliveryDetails.className =
        'ordering-dog__delivery-details';
      deliveryDetails.dataset.orderingDeliveryCopy = '';
    }

    // Always directly above the button.
    const checkout = summaryStep.querySelector('[data-checkout]');

    if (checkout) {
      checkout.insertAdjacentElement('beforebegin', deliveryDetails);
    } else {
      list.insertAdjacentElement('afterend', deliveryDetails);
    }

    deliveryDetails.hidden = false;

    const longWait =
      info.daysFromToday > 7 && hasThisWeeksCutoffPassed();

    deliveryDetails.innerHTML = `
      <p>
        Delivered ${escapeHTML(info.dayName)}s.
        First delivery: ${escapeHTML(info.firstDeliveryText)}.
      </p>

      ${longWait ? `
        <p class="ordering-dog__delivery-wait">
          That's ${info.daysFromToday} days from today because this
          week's order cutoff (Wednesday 11:59pm) has passed.
        </p>
      ` : ''}
    `;
  }


  /*
   * ============================================================
   * DELIVERY ROUTES, ZIP CHECK
   * ============================================================
   */

  const zipToRoute = new Map();

  (function buildZipIndex() {
    if (!deliveryZones || typeof deliveryZones !== 'object') return;

    Object.keys(deliveryZones).forEach(routeKey => {
      const route = ROUTES[normalize(routeKey)];

      if (!route) {
        console.warn(
          `[ordering-dog] Unknown route "${routeKey}" in delivery_zip_codes`
        );
        return;
      }

      normalizeArray(deliveryZones[routeKey]).forEach(zip => {
        const clean = cleanZip(zip);
        if (clean) zipToRoute.set(clean, route);
      });
    });

    if (!zipToRoute.size) {
      console.warn(
        '[ordering-dog] No delivery ZIP codes found. ' +
        'Fill the product metafield custom.delivery_zip_codes.'
      );
    }
  })();

  function cleanZip(value) {
    const match = String(value ?? '').match(/\d{5}/);
    return match ? match[0] : '';
  }

  function getRouteForZip(zip) {
    return zipToRoute.get(cleanZip(zip)) || null;
  }

  /*
   * First delivery = the route's day after the next cutoff.
   * Returns a Date at noon UTC on the Pacific calendar day.
   */
  function getFirstDeliveryDate(route) {
    const cutoff = getNextWednesday();
    const date = new Date(cutoff);

    date.setUTCDate(
      date.getUTCDate() + route.daysAfterCutoff
    );

    return date;
  }

  /*
   * Whole days from today (Pacific) to a delivery date.
   */
  function getDaysFromToday(date) {
    const today = getPacificParts(new Date());

    const todayUTC = Date.UTC(
      Number(today.year),
      Number(today.month) - 1,
      Number(today.day),
      12
    );

    return Math.round((date.getTime() - todayUTC) / 86400000);
  }

  function toISODate(date) {
    return date.toISOString().slice(0, 10);
  }

  function getDeliveryInfo() {
    if (!state.route) return null;

    const firstDelivery = getFirstDeliveryDate(state.route);

    return {
      routeKey: state.route.key,
      firstDeliveryISO: toISODate(firstDelivery),
      zip: state.zip,
      route: state.route,
      dayName: state.route.dayName,
      area: state.route.area,
      firstDelivery,
      firstDeliveryText: formatDate(firstDelivery),
      daysFromToday: getDaysFromToday(firstDelivery)
    };
  }

  function rememberZip(zip) {
    try {
      sessionStorage.setItem(ZIP_STORAGE_KEY, zip);
    } catch (error) {
      // Storage can be blocked; the ZIP still works for this page.
    }
  }

  function forgetZip() {
    try {
      sessionStorage.removeItem(ZIP_STORAGE_KEY);
    } catch (error) {}
  }

  function getRememberedZip() {
    try {
      return cleanZip(sessionStorage.getItem(ZIP_STORAGE_KEY));
    } catch (error) {
      return '';
    }
  }

  /*
   * The step a new order starts on.
   */
  function getStartStep() {
    return state.route ? 'feed' : 'zip';
  }

  /*
   * 3.5 "Delivering Tuesdays to 97214 · Change"
   */
  function updateDeliveryLine() {
    const line = $('[data-delivery-line]');
    if (!line) return;

    const show =
      Boolean(state.route) && state.currentStep !== 'zip';

    line.hidden = !show;

    if (!show) return;

    const text = $('[data-delivery-line-text]', line);

    if (text) {
      text.textContent =
        `Delivering ${state.route.dayName}s to ${state.zip}`;
    }
  }

  function setZipMessage(type, html) {
    const inArea = $('[data-zip-in-area]');
    const outArea = $('[data-zip-out-area]');
    const error = $('[data-zip-error]');

    if (inArea) inArea.hidden = type !== 'in';
    if (outArea) outArea.hidden = type !== 'out';
    if (error) {
      error.hidden = type !== 'error';
      if (type === 'error') error.textContent = html;
    }
  }

  /*
   * 3.3 / 3.4
   */
  function checkZip() {
    const input = $('[data-zip-input]');
    const zip = cleanZip(input ? input.value : '');

    if (!zip) {
      setZipMessage('error', 'Please enter a 5-digit ZIP code.');
      if (input) input.focus();
      return;
    }

    if (input) input.value = zip;

    const route = getRouteForZip(zip);

    if (!route) {
      state.zip = '';
      state.route = null;
      forgetZip();

      const outText = $('[data-zip-out-text]');
      if (outText) {
        outText.textContent =
          `We don't deliver to ${zip} yet. ` +
          "Leave your email and we'll tell you when we do.";
      }

      const outForm = $('[data-waitlist-area]');
      const outThanks = $('[data-waitlist-area-thanks]');
      if (outForm) outForm.hidden = false;
      if (outThanks) outThanks.hidden = true;

      setAreaWaitlistTags(zip);
      setZipMessage('out');
      return;
    }

    state.zip = zip;
    state.route = route;
    rememberZip(zip);

    const info = getDeliveryInfo();

    const inText = $('[data-zip-in-text]');
    if (inText) {
      inText.textContent =
        `You're on our ${info.dayName} ${info.area} route. ` +
        `Your first delivery would be ${info.firstDeliveryText}.`;
    }

    setZipMessage('in');

    const continueButton = $('[data-zip-continue]');
    if (continueButton) continueButton.focus();
  }

  function continueFromZip() {
    if (!state.route) return;

    state.stepHistory = [];

    // After "Change", return to where the customer was.
    const next =
      state.dogs.length && state.stepBeforeZip === 'summary'
        ? 'summary'
        : (state.stepBeforeZip && state.stepBeforeZip !== 'zip'
            ? state.stepBeforeZip
            : 'feed');

    state.stepBeforeZip = null;

    showStep(next, { pushHistory: false });
  }

  function changeZip() {
    state.stepBeforeZip = state.currentStep;

    const input = $('[data-zip-input]');
    if (input) input.value = state.zip || '';

    setZipMessage('none');

    showStep('zip', { pushHistory: false });

    if (input) input.focus({ preventScroll: true });
  }

  /*
   * 3.4 Waitlist sign-up.
   *
   * The email box is a real Shopify customer form
   * ({% form 'customer' %} in the liquid), so Shopify's spam
   * protection works and the customer is created with the tags.
   * The JS only fills in the tags (waitlist-area, zip-XXXXX)
   * before the form is sent, and shows the right message when
   * Shopify returns the customer to this page.
   */
  const WAITLIST_STORAGE_KEY = 'pp_pending_waitlist';

  function savePendingWaitlist(data) {
    try {
      sessionStorage.setItem(WAITLIST_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {}
  }

  function clearPendingWaitlist() {
    try {
      sessionStorage.removeItem(WAITLIST_STORAGE_KEY);
    } catch (error) {}
  }

  function getPendingWaitlist() {
    try {
      return JSON.parse(sessionStorage.getItem(WAITLIST_STORAGE_KEY) || 'null');
    } catch (error) {
      return null;
    }
  }

  function setAreaWaitlistTags(zip) {
    const tagsInput = $('[data-waitlist-area-tags]');
    if (tagsInput) {
      tagsInput.value = zip
        ? `waitlist-area, zip-${zip}`
        : 'waitlist-area';
    }
  }

  function showOutOfArea(zip) {
    const input = $('[data-zip-input]');
    if (input) input.value = zip || '';

    const outText = $('[data-zip-out-text]');
    if (outText) {
      outText.textContent =
        `We don't deliver to ${zip} yet. ` +
        "Leave your email and we'll tell you when we do.";
    }

    setAreaWaitlistTags(zip);
    setZipMessage('out');
  }

  function bindAreaWaitlistForm() {
    const container = $('[data-waitlist-area]');
    const form = container && container.querySelector('form');

    if (!form) return;

    form.addEventListener('submit', event => {
      const zip = cleanZip(($('[data-zip-input]') || {}).value);
      const emailInput = $('[data-waitlist-area-email]');
      const email = String(emailInput ? emailInput.value : '').trim();

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        event.preventDefault();
        if (emailInput) {
          emailInput.setCustomValidity('Please enter a valid email.');
          emailInput.reportValidity();
          emailInput.setCustomValidity('');
        }
        return;
      }

      setAreaWaitlistTags(zip);
      savePendingWaitlist({ kind: 'area', zip, email });
      // No preventDefault: Shopify handles the submit.
    });
  }

  /*
   * 4.6 Puppy reminder sign-up (same real-form approach as 3.4).
   * Tags: waitlist-puppy, pup-birthday-YYYY-MM
   * Note: "Pup's birthday: March 2026"
   */
  const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  function fillPuppyYearOptions() {
    const select = $('[data-waitlist-puppy-year]');
    if (!select || select.options.length > 1) return;

    const year = Number(getPacificParts(new Date()).year);

    // A pup under 1 was born this year or last year.
    [year, year - 1].forEach(value => {
      const option = document.createElement('option');
      option.value = String(value);
      option.textContent = String(value);
      select.appendChild(option);
    });
  }

  function bindPuppyWaitlistForm() {
    const container = $('[data-waitlist-puppy]');
    const form = container && container.querySelector('form');

    if (!form) return;

    fillPuppyYearOptions();

    form.addEventListener('submit', event => {
      const emailInput = $('[data-waitlist-puppy-email]');
      const monthSelect = $('[data-waitlist-puppy-month]');
      const yearSelect = $('[data-waitlist-puppy-year]');
      const error = $('[data-waitlist-puppy-check]');

      const email = String(emailInput ? emailInput.value : '').trim();
      const month = Number(monthSelect ? monthSelect.value : 0);
      const year = Number(yearSelect ? yearSelect.value : 0);

      let message = '';

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        message = 'Please enter a valid email.';
      } else if (!month || !year) {
        message = "Please choose your pup's birthday month and year.";
      }

      if (message) {
        event.preventDefault();
        if (error) {
          error.textContent = message;
          error.hidden = false;
        }
        return;
      }

      if (error) error.hidden = true;

      const monthText = MONTH_NAMES[month - 1];
      const monthCode = String(month).padStart(2, '0');
      const name = getDogName();

      const tags = $('[data-waitlist-puppy-tags]');
      if (tags) {
        tags.value = `waitlist-puppy, pup-birthday-${year}-${monthCode}`;
      }

      const note = $('[data-waitlist-puppy-note]');
      if (note) {
        note.value =
          `Pup's birthday: ${monthText} ${year}` +
          (name ? ` (${name})` : '');
      }

      savePendingWaitlist({ kind: 'puppy', email, name });
      // No preventDefault: Shopify handles the submit.
    });
  }

  /*
   * Shopify reloads the page after a sign-up form is sent:
   * - success: ?customer_posted=true (and form.posted_successfully?)
   * - error: the form shows Shopify's error message
   * Put the customer back where they were.
   * Returns 'area', 'puppy' or ''.
   */
  function restoreWaitlistAfterReload() {
    const pending = getPendingWaitlist();
    if (!pending) return '';

    const kind = pending.kind === 'puppy' ? 'puppy' : 'area';
    const posted =
      /customer_posted=true/.test(window.location.search) ||
      Boolean($(`[data-waitlist-${kind}-posted]`));
    const error = $(`[data-waitlist-${kind}-error]`);

    if (!posted && !error) {
      clearPendingWaitlist();
      return '';
    }

    if (kind === 'area') {
      showOutOfArea(pending.zip);
    } else {
      state.feed = 'Complete Meals';
      state.age = 'Puppy';
      state.dogName = pending.name || '';
      setSelected('[data-age]', 'data-age', 'Puppy');
      updatePuppyMessage();
    }

    const formBox = $(`[data-waitlist-${kind}]`);
    const thanks = $(`[data-waitlist-${kind}-thanks]`);

    if (posted) {
      clearPendingWaitlist();
      if (formBox) formBox.hidden = true;
      if (thanks) thanks.hidden = false;
    } else {
      const emailInput = $(`[data-waitlist-${kind}-email]`);
      if (emailInput && pending.email) emailInput.value = pending.email;
      if (formBox) formBox.hidden = false;
      if (thanks) thanks.hidden = true;
    }

    return kind;
  }

  /*
   * 3.1 Route banner from the ad link (?route=salem).
   */
  function showRouteBanner() {
    const params = new URLSearchParams(window.location.search);
    const route = ROUTES[normalize(params.get('route'))];

    if (!route) return false;

    if (!document.querySelector('[data-route-banner]')) {
      const banner = document.createElement('div');
      banner.className = 'ordering-dog-route-banner';
      banner.dataset.routeBanner = '';
      banner.setAttribute('role', 'note');
      banner.innerHTML =
        `<p class="ordering-dog-route-banner__text">${escapeHTML(route.banner)}</p>`;

      const section =
        root.closest('.shopify-section') ||
        root.closest('section') ||
        root;

      section.insertAdjacentElement('beforebegin', banner);
    }

    return true;
  }

  /*
   * Cart attributes, so the ZIP and route reach the order.
   */
  async function saveDeliveryToCart() {
    const info = getDeliveryInfo();
    if (!info) return;

    const url = window.Shopify?.routes?.root
      ? `${window.Shopify.routes.root}cart/update.js`
      : '/cart/update.js';

    try {
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            'Delivery ZIP': info.zip,
            'Delivery route': info.area,
            'Delivery day': info.dayName,
            'First delivery': info.firstDeliveryText,
            '_first_delivery_date': info.firstDeliveryISO,
            '_delivery_route': info.routeKey
          }
        })
      });
    } catch (error) {
      console.warn('[ordering-dog] Could not save delivery to cart', error);
    }
  }

  /*
   * Checkout URL with the ZIP pre-filled where Shopify allows it.
   */
  function getCheckoutUrl() {
    const base = window.Shopify?.routes?.root
      ? `${window.Shopify.routes.root}checkout`
      : '/checkout';

    if (!state.zip) return base;

    return `${base}?checkout[shipping_address][zip]=${encodeURIComponent(state.zip)}`;
  }

  function bindZipStep() {
    const input = $('[data-zip-input]');
    const checkButton = $('[data-zip-check]');
    const continueButton = $('[data-zip-continue]');

    if (input) {
      input.addEventListener('keydown', event => {
        if (event.key === 'Enter') {
          event.preventDefault();
          checkZip();
        }
      });

      input.addEventListener('input', () => {
        input.value = input.value.replace(/\D/g, '').slice(0, 5);
      });
    }

    if (checkButton) {
      checkButton.addEventListener('click', event => {
        event.preventDefault();
        checkZip();
      });
    }

    if (continueButton) {
      continueButton.addEventListener('click', event => {
        event.preventDefault();
        continueFromZip();
      });
    }

    $$('[data-delivery-change]').forEach(link => {
      link.addEventListener('click', event => {
        event.preventDefault();
        changeZip();
      });
    });

    bindAreaWaitlistForm();
    bindPuppyWaitlistForm();
  }


  /*
   * ============================================================
   * FEEDING LOOKUP
   * ============================================================
   */

  function getLookup() {
    if (
      metafields &&
      typeof metafields === 'object' &&
      !Array.isArray(metafields)
    ) {
      /*
       * If the JSON metafield itself is nested under a known key,
       * handle that too.
       */

      if (
        metafields.feedingLookup &&
        typeof metafields.feedingLookup === 'object'
      ) {
        return metafields.feedingLookup;
      }

      if (
        metafields.lookup &&
        typeof metafields.lookup === 'object'
      ) {
        return metafields.lookup;
      }
    }

    return FALLBACK_LOOKUP;
  }

  const feedingLookup =
  metafields?.feedingLookup || FALLBACK_LOOKUP;

  function findObjectKey(object, wanted) {
    if (!object || typeof object !== 'object') {
      return null;
    }

    const wantedNormalized =
      normalize(wanted);

    return Object.keys(object).find(key => {
      return normalize(key) === wantedNormalized;
    }) || null;
  }

  function getLookupAmount(
    weight,
    age,
    activity
  ) {
    const lookup =
      feedingLookup || FALLBACK_LOOKUP;

    const weightKey =
      findObjectKey(lookup, weight);

    if (!weightKey) {
      return null;
    }

    const weightObject =
      lookup[weightKey];

    const ageKey =
      findObjectKey(weightObject, age);

    if (!ageKey) {
      return null;
    }

    const ageObject =
      weightObject[ageKey];

    const activityKey =
      findObjectKey(
        ageObject,
        activity
      );

    if (!activityKey) {
      return null;
    }

    const amount =
      Number(ageObject[activityKey]);

    if (!Number.isFinite(amount)) {
      return null;
    }

    return amount;
  }


  /*
   * ============================================================
   * VARIANT SYNCHRONIZATION
   * ============================================================
   */

  function syncVariant() {
    const amount = Number(state.ounces);

    const variant =
      variantByDailyAmount.get(amount);

    state.variantId =
      variant ? variant.id : null;

    state.variantPrice =
      variant ? variant.price : 0;

    const hiddenVariant =
      $('[data-ordering-variant-id]');

    if (hiddenVariant) {
      hiddenVariant.value =
        variant ? variant.id : '';
    }

    return variant || null;
  }

  function validateVariant(ounces) {
    const variant =
      variantByDailyAmount.get(
        Number(ounces)
      );

    if (!variant) {
      return {
        valid: false,
        message:
          `There is no Daily Amount variant for ${ounces} oz/day.`
      };
    }

    if (!variant.available) {
      return {
        valid: false,
        message:
          `The ${ounces} oz/day option is currently unavailable.`
      };
    }

    return {
      valid: true,
      variant
    };
  }


  /*
   * ============================================================
   * STEP NAVIGATION
   * ============================================================
   */

  /*
   * 4.8 "Step 2 of 5". The path depends on the choices made:
   *   Complete Meals:        feed, weight, age, activity, result
   *   Known daily amount:    feed, weight, amount, result
   *   Topper:                feed, topper, result
   */
  function getStepPath() {
    if (state.feed === 'Topper') {
      return ['feed', 'topper', 'result'];
    }

    if (state.knownAmountPath) {
      return ['feed', 'weight', 'amount', 'result'];
    }

    return ['feed', 'weight', 'age', 'activity', 'result'];
  }

  function updateStepCounter(section, step) {
    if (!section) return;

    let counter = section.querySelector('[data-step-count]');

    if (step === 'zip' || step === 'summary') {
      if (counter) counter.hidden = true;
      return;
    }

    const path = getStepPath();
    let position = path.indexOf(step);

    // "Use a different amount" from the result: same step number.
    if (position === -1) position = path.indexOf('result');

    if (!counter) {
      counter = document.createElement('p');
      counter.className = 'ordering-dog__step-count';
      counter.dataset.stepCount = '';

      const content =
        section.querySelector('.ordering-dog__step-content') || section;
      const back = content.querySelector(':scope > [data-back]');

      // Top of the step, just below the Back link.
      if (back) {
        back.insertAdjacentElement('afterend', counter);
      } else {
        content.insertBefore(counter, content.firstChild);
      }
    }

    counter.hidden = false;
    counter.textContent = `Step ${position + 1} of ${path.length}`;
  }

  /*
   * 4.10 Scroll so the new question sits at the top of the
   * screen, below any sticky header.
   */
  function getStickyHeaderOffset() {
    const header = document.querySelector(
      'sticky-header, .shopify-section-header-sticky, .section-header, header'
    );

    if (!header) return 0;

    const rect = header.getBoundingClientRect();
    const style = window.getComputedStyle(header);
    const sticks =
      style.position === 'sticky' ||
      style.position === 'fixed' ||
      (header.parentElement &&
        /sticky|fixed/.test(
          window.getComputedStyle(header.parentElement).position
        ));

    return sticks && rect.bottom > 0 ? rect.bottom : 0;
  }

  function scrollToStep(section) {
    const target = section || root;

    const nextFrame =
      window.requestAnimationFrame
        ? callback => window.requestAnimationFrame(callback)
        : callback => setTimeout(callback, 0);

    // Wait for the new step to be laid out before measuring.
    nextFrame(() => {
      if (!target.getBoundingClientRect || !window.scrollTo) return;

      const rect = target.getBoundingClientRect();
      const offset = getStickyHeaderOffset() + 12;
      const isPhone = window.matchMedia
        ? window.matchMedia('(max-width: 749px)').matches
        : false;

      // On larger screens only scroll when the question is off screen.
      const visible =
        rect.top >= offset && rect.top < window.innerHeight * 0.4;

      if (!isPhone && visible) return;

      window.scrollTo({
        top: Math.max(0, window.scrollY + rect.top - offset),
        behavior: 'smooth'
      });
    });
  }

  function showStep(
    step,
    options = {}
  ) {
    const {
      pushHistory = true,
      scroll = true
    } = options;

    const previousStep =
      state.currentStep;

    if (
      pushHistory &&
      previousStep &&
      previousStep !== step
    ) {
      state.stepHistory.push(
        previousStep
      );
    }

    state.currentStep = step;

    $$('.ordering-dog__step').forEach(
      section => {
        const active =
          section.dataset.step === step;

        section.hidden = !active;

        if (active) {
          section.setAttribute(
            'aria-current',
            'step'
          );
        } else {
          section.removeAttribute(
            'aria-current'
          );
        }
      }
    );

    updateBackButtons();
    updateDeliveryLine();
    updateDogNameOutputs();
    updateDeliveryDates();

    if (step === 'result') {
      updateResult();
    }

    if (step === 'summary') {
      renderSummary();
    }

    updateMobileCheckout();

    const activeSection = $$('.ordering-dog__step').find(
      section => section.dataset.step === step
    );

    updateStepCounter(activeSection, step);

    if (scroll) {
      scrollToStep(activeSection);
    }
  }

  function goBack() {
    if (
      !state.stepHistory.length
    ) {
      return;
    }

    const previous =
      state.stepHistory.pop();

    showStep(
      previous,
      {
        pushHistory: false
      }
    );
  }

  function updateBackButtons() {
    const isFirstStep =
      state.currentStep === 'feed' ||
      state.currentStep === 'zip';
    const canGoBack =
        !isFirstStep && state.stepHistory.length > 0;

    $$('[data-back]').forEach(button => {
        button.hidden = !canGoBack;
    });
    }


  /*
   * ============================================================
   * SELECTION UI
   * ============================================================
   */

  function setSelected(
    selector,
    attribute,
    value
  ) {
    $$(selector).forEach(
      button => {
        const selected =
          normalize(
            button.getAttribute(attribute)
          ) === normalize(value);

        button.classList.toggle(
          'is-selected',
          selected
        );

        button.setAttribute(
          'aria-pressed',
          selected
            ? 'true'
            : 'false'
        );
      }
    );
  }


  /*
   * ============================================================
   * PUPPY MESSAGE
   * ============================================================
   */

  function updatePuppyMessage() {
    const message =
      $('[data-puppy-message]');

    if (!message) {
      return;
    }

    message.hidden =
      state.age !== 'Puppy';
  }


  /*
   * ============================================================
   * LINE ITEM / DOG OBJECT
   * ============================================================
   */

  function getFeedValue() {
    return state.feed === 'Complete Meals'
      ? 'Full Meal'
      : 'Topper';
  }

  function getDaysOfFood() {
    return clampDays(state.daysPerWeek);
  }

  /*
   * Change the days a week and refresh every price shown.
   * The days picker (step 5.2) calls this.
   */
  function setDaysPerWeek(value) {
    state.daysPerWeek = clampDays(value);

    setSelected(
      '[data-days-per-week]',
      'data-days-per-week',
      String(state.daysPerWeek)
    );

    updateResult();
    updateAmountUI({ preserveInput: true });
  }

  function buildDogObject() {
    const ounces =
      Number(state.ounces);

    const plan =
      calculatePlan(ounces, state.daysPerWeek);

    const variant =
      syncVariant();

    return {
      dogName: getDogName(),

      customDogName: String(state.dogName || '').trim(),

      recipe: RECIPE_NAME,

      feed: state.feed,

      feedType: getFeedValue(),

      weight: state.weight,

      age: state.age,

      activity: state.activity,

      ounces,

      daysFood: plan.daysPerWeek,

      customAmount: Boolean(state.customAmount),

      knownAmountPath: Boolean(state.knownAmountPath),

      meals: plan.meals,

      containersPerDay: plan.containersPerDay,

      containersPerWeek: plan.containersPerWeek,

      dailyPrice: plan.dailyPrice,

      weeklyPrice: plan.weeklyPrice,

      variantId:
        variant ? variant.id : null,

      variantPrice:
        variant
          ? variant.price
          : 0
    };
  }


  /*
   * ============================================================
   * RESULT
   * ============================================================
   */

  
  /*
   * 5.1 / 5.3 Result card.
   *
   * Complete Meals (recommended):
   *   We recommend about 20 oz a day (570 g)
   *   $175.00 a week · $25.00 a day
   *   7 days of food · 14 meals if you feed twice a day · 7 containers
   *
   * Topper:
   *   8 oz a day (230 g) as a topper
   *   $40.00 a week · $10.00 a day
   *   4 days of food · 4 containers
   *
   * Customer's own amount: "Bo's daily amount: 58 oz a day (1,640 g)"
   */
  function capitalize(text) {
    return text.charAt(0).toLocaleUpperCase() + text.slice(1);
  }

  function updateResult() {
    const ounces = Number(state.ounces);

    syncVariant();

    const plan = calculatePlan(ounces, state.daysPerWeek);
    const isTopper = state.feed === 'Topper';

    // "570 g" stays on one line.
    const amountHTML =
      `${ounces} oz a day ` +
      `<span class="ordering-dog__nowrap">(${plan.grams.toLocaleString('en-US')} g)</span>`;

    let headingHTML;

    if (isTopper) {
      headingHTML = `${amountHTML} as a topper`;
    } else if (state.customAmount) {
      headingHTML =
        `${escapeHTML(capitalize(getDogPossessive()))} daily amount: ${amountHTML}`;
    } else {
      headingHTML = `We recommend about ${amountHTML}`;
    }

    $$('[data-result-heading]').forEach(el => {
      el.innerHTML = headingHTML;
    });

    $$('[data-result-price-line]').forEach(el => {
      el.textContent =
        `$${formatMoney(plan.weeklyPrice)} a week · ` +
        `$${formatMoney(plan.dailyPrice)} a day`;
    });

    $$('[data-result-plan-amount]').forEach(el => {
      el.textContent = isTopper
        ? `${pluralize(plan.daysPerWeek, 'day')} of food · ` +
          pluralize(plan.containersPerWeek, 'container')
        : formatPlanAmount(plan, {
            mealsSuffix: ' if you feed twice a day'
          });
    });

    // "Use a different amount" and the starting-amount note are for
    // Complete Meals only. Toppers pick 4, 8 or 12 oz.
    $$('[data-change-amount]').forEach(el => {
      el.hidden = isTopper;
    });

    $$('[data-result-starting-note]').forEach(el => {
      el.hidden = isTopper || state.customAmount;
    });

    // Older markup, kept working.
    const amountElement = $('[data-result-amount]');
    if (amountElement) amountElement.textContent = ounces;

    const gramsElement = $('[data-result-grams]');
    if (gramsElement) gramsElement.textContent = plan.grams;

    $$('[data-result-daily-price]').forEach(el => {
      el.textContent = formatMoney(plan.dailyPrice);
    });

    $$('[data-result-weekly-price]').forEach(el => {
      el.textContent = formatMoney(plan.weeklyPrice);
    });

    $$('[data-result-days]').forEach(el => {
      el.textContent = plan.daysPerWeek;
    });

    // 5.2 Days buttons show the current choice.
    setSelected(
      '[data-days-per-week]',
      'data-days-per-week',
      String(plan.daysPerWeek)
    );

    updateDogNameOutputs();
  }



  /*
   * ============================================================
   * AMOUNT CONTROL
   * ============================================================
   */
  
  
  let amountUnit = 'oz';

  function normalizeAmount(value, unit = amountUnit) {
    let amount = Number.parseFloat(value);

    if (!Number.isFinite(amount)) {
      amount = DEFAULT_AMOUNT;
    }

    // Convert grams to ounces before finding the supported variant.
    if (unit === 'grams') {
      amount = amount / 28.349523125;
    }

    // Shopify has variants only for even-ounce amounts.
    amount = Math.round(amount / AMOUNT_STEP) * AMOUNT_STEP;

    return Math.max(
      MIN_OUNCES,
      Math.min(MAX_OUNCES, amount)
    );
  }

  function updateAmountUI(options = {}) {
    const { preserveInput = false } = options;

    const input = $('[data-amount-input]');
    const amount = Number(state.ounces);

    if (input) {
      input.min = String(
        amountUnit === 'grams'
          ? Math.round(MIN_OUNCES * 28.349523125)
          : MIN_OUNCES
      );

      input.max = String(
        amountUnit === 'grams'
          ? Math.round(MAX_OUNCES * 28.349523125)
          : MAX_OUNCES
      );

      input.step = String(
        amountUnit === 'grams' ? 50 : AMOUNT_STEP
      );

      input.inputMode = 'decimal';

      // Don't overwrite the input while the customer is typing.
      if (!preserveInput) {
        input.value = amountUnit === 'grams'
          ? ouncesToGrams(amount)
          : amount;
      }
    }

    $$('[data-amount-unit]').forEach(button => {
      const selected = button.dataset.amountUnit === amountUnit;

      button.setAttribute('aria-pressed', String(selected));
      button.classList.toggle('is-selected', selected);
    });

    const unitLabel = $('[data-amount-unit-label]');
    if (unitLabel) {
      unitLabel.textContent = amountUnit;
    }

    const gramsElement = $('[data-amount-grams]');
    if (gramsElement) {
      gramsElement.textContent = ouncesToGrams(amount);
    }

    const priceElement = $('[data-amount-daily-price]');
    if (priceElement) {
      priceElement.textContent = formatMoney(getDailyPrice(amount));
    }

    // 5.5 Weekly price for the chosen days.
    $$('[data-amount-weekly-price]').forEach(el => {
      el.textContent = formatMoney(
        getWeeklyPrice(amount, state.daysPerWeek)
      );
    });

    $$('[data-amount-days]').forEach(el => {
      el.textContent = pluralize(clampDays(state.daysPerWeek), 'day');
    });

    const largeAmountValue = $('[data-large-amount-value]');
    if (largeAmountValue) {
      largeAmountValue.textContent = amount;
    }

    const largeAmount = $('[data-large-amount]');
    if (largeAmount) {
      largeAmount.hidden = amount <= 100;
    }

    syncVariant();
  }

  function setAmount(value, unit = amountUnit) {
    state.ounces = normalizeAmount(value, unit);
    state.largeAmountConfirmed = state.ounces <= 100;

    updateAmountUI();
  }



  /*
   * ============================================================
   * SAVE CURRENT DOG
   * ============================================================
   */

  function saveCurrentDog() {
    const dog =
      buildDogObject();

    if (!dog.variantId) {
      return null;
    }

    if (
      state.editingDogIndex !== null
    ) {
      state.dogs[
        state.editingDogIndex
      ] = dog;

      state.editingDogIndex = null;
    } else {
      state.dogs.push(dog);
    }

    return dog;
  }


  /*
   * ============================================================
   * EDIT DOG
   * ============================================================
   */

  function loadDogIntoState(
    dog,
    index
  ) {
    state.dogName = String(dog.customDogName || '').trim();

    state.feed =
      dog.feed;

    state.weight =
      dog.weight || '';

    state.age =
      dog.age || '';

    state.activity =
      dog.activity || '';

    state.ounces =
      Number(dog.ounces) ||
      DEFAULT_AMOUNT;

    state.daysPerWeek =
      clampDays(dog.daysFood || DEFAULT_DAYS_PER_WEEK);

    state.customAmount = Boolean(dog.customAmount);
    state.knownAmountPath = Boolean(dog.knownAmountPath);

    setSelected(
      '[data-days-per-week]',
      'data-days-per-week',
      String(state.daysPerWeek)
    );

    state.largeAmountConfirmed =
      state.ounces <= 100;

    state.editingDogIndex =
      index;

    state.stepHistory = [];

    const nameInput =
      $('[data-dog-name]');

    if (nameInput) {
      nameInput.value =
        state.dogName;
    }

    setSelected(
      '[data-feed-choice]',
      'data-feed-choice',
      state.feed
    );

    setSelected(
      '[data-weight]',
      'data-weight',
      state.weight
    );

    setSelected(
      '[data-age]',
      'data-age',
      state.age
    );

    setSelected(
      '[data-activity]',
      'data-activity',
      state.activity
    );

    setSelected(
      '[data-topper-amount]',
      'data-topper-amount',
      String(state.ounces)
    );

    updateAmountUI();
    updatePuppyMessage();
    updateDogNameOutputs();

    /*
     * Editing always starts at the beginning.
     * Existing selections are retained.
     */
    showStep(
      'feed',
      {
        pushHistory: false
      }
    );
  }


  /*
   * ============================================================
   * REMOVE DOG
   * ============================================================
   */

  function removeDog(index) {
    if (
      index < 0 ||
      index >= state.dogs.length
    ) {
      return;
    }

    state.dogs.splice(index, 1);

    renderSummary();
    updateMobileCheckout();
  }


  /*
   * ============================================================
   * SUMMARY
   * ============================================================
   */

  function renderSummary() {
    const list = $('[data-dogs-list]');
    if (!list) return;

    const summaryStep = list.closest('[data-step="summary"]') || root;
    list.innerHTML = '';

    // Remove old/repeated summary content.
    summaryStep.querySelectorAll(
      '[data-weekly-summary-intro], ' +
      '.ordering-dog__summary-intro, ' +
      '.ordering-dog__small-text, ' +
      '.ordering-dog__delivery-details, ' +
      '[data-generated-summary-total], ' +
      '[data-weekly-summary-footer]'
    ).forEach(el => el.remove());

    summaryStep.querySelectorAll('p').forEach(el => {
      const text = (el.textContent || '').trim();

      if (
        /^One plan:\s*weekly delivery\.?$/i.test(text) ||
        /Just want to try one week/i.test(text) ||
        /Your first week is refundable/i.test(text)
      ) {
        el.remove();
      }
    });

    const heading = summaryStep.querySelector('h2');
    if (heading) heading.textContent = 'Your weekly delivery';

    // One white card for each dog.
    state.dogs.forEach((dog, index) => {
      const card = document.createElement('article');
      card.className = 'ordering-dog__dog-card';

      const feedLabel =
        /full meal|complete meals/i.test(
          String(dog.feed || dog.feedType || '')
        )
          ? 'Complete Meals'
          : 'Topper';

      const plan = calculatePlan(
        dog.ounces,
        dog.daysFood || DEFAULT_DAYS_PER_WEEK
      );

      const grams = plan.grams;

      // 4.2 No name: "Your dog". Never "Dog 1" for the customer.
      const displayName =
        dog.customDogName ||
        'Your dog';

      // 6.1 "Rory · Complete Meals · 20 oz a day (570 g)"
      // Toppers: no meals line, same as their result card (5.3).
      const amountLine = feedLabel === 'Topper'
        ? `${pluralize(plan.daysPerWeek, 'day')} of food · ` +
          pluralize(plan.containersPerWeek, 'container')
        : formatPlanAmount(plan);

      card.innerHTML = `
        <div class="ordering-dog__dog-card-header">
          <div class="ordering-dog__dog-card-title-wrap">
            <h3 data-dog-display-name>
              ${escapeHTML(displayName)} · ${escapeHTML(feedLabel)} ·
              ${Number(dog.ounces)} oz a day
              <span class="ordering-dog__nowrap">(${grams.toLocaleString('en-US')} g)</span>
            </h3>
          </div>

          <div class="ordering-dog__dog-card-actions">
            <button type="button" data-edit-dog="${index}">
              Edit
            </button>

            <button type="button" data-remove-dog="${index}">
              Remove
            </button>
          </div>
        </div>

        <p class="ordering-dog__dog-card-amount">
          ${escapeHTML(amountLine)}
        </p>

        <p class="ordering-dog__dog-card-price">
          $${formatMoney(plan.weeklyPrice)} a week
        </p>
      `;

      list.appendChild(card);
    });

    // Keep the Add another dog button directly after the cards.
    const addDogButton = summaryStep.querySelector('[data-add-dog]');

    if (addDogButton) {
      addDogButton.textContent = '+ Add another dog';
      list.insertAdjacentElement('afterend', addDogButton);
    }

    // Calculate one combined total for all dogs.
    const totals_ = calculateOrderTotals(state.dogs);

    const totalText = formatMoney(totals_.total);

    // Replace old totals and remove the sales tax row.
    let totals = summaryStep.querySelector(
      '.ordering-dog__summary-totals'
    );

    if (!totals) {
      totals = document.createElement('div');
      totals.className = 'ordering-dog__summary-totals';

      (addDogButton || list).insertAdjacentElement(
        'afterend',
        totals
      );
    }

    totals.innerHTML = `
      <div class="ordering-dog__summary-row">
        <span>Delivery</span>
        <strong data-summary-delivery>
          ${totals_.freeDelivery
            ? 'Free'
            : `$${formatMoney(totals_.deliveryFee)}`}
        </strong>
      </div>

      ${totals_.freeDelivery ? '' : `
        <p class="ordering-dog__free-delivery-nudge" data-free-delivery-nudge>
          Add $${formatMoney(totals_.amountToFreeDelivery)} more each week for free delivery.
        </p>
      `}

      <div class="ordering-dog__summary-row ordering-dog__summary-row--total">
        <span>Total</span>
        <strong>
          $<span data-summary-total>${totalText}</span> a week
        </strong>
      </div>
    `;

    // Retain one existing checkout button so its click handler still works.
    const checkoutButtons = Array.from(
      summaryStep.querySelectorAll('[data-checkout]')
    );

    const checkout = checkoutButtons.shift() || null;
    checkoutButtons.forEach(button => button.remove());

    if (checkout) {
      // 6.5 One named dog: "Start Rory's weekly delivery".
      // Two or more dogs, or no name: "Start weekly delivery".
      const onlyDog = state.dogs.length === 1 ? state.dogs[0] : null;
      const onlyName = onlyDog
        ? String(onlyDog.customDogName || '').trim()
        : '';

      const label = onlyName
        ? `Start ${escapeHTML(getDogPossessive(onlyName))} weekly delivery`
        : 'Start weekly delivery';

      checkout.innerHTML =
        `${label} · $` +
        `<span data-checkout-total>${totalText}</span> a week`;

      checkout.classList.add('ordering-dog__checkout-button');

      totals.insertAdjacentElement('afterend', checkout);
    }

    // Hide the second, sticky checkout button.
    const mobileBar = $('[data-mobile-checkout]');
    if (mobileBar) mobileBar.hidden = true;

    $$('[data-mobile-checkout-button]').forEach(button => {
      button.hidden = true;
    });

    // Refund policy and supporting information.
    let reassurance = summaryStep.querySelector(
      '[data-ordering-reassurance]'
    );

    if (!reassurance) {
      reassurance = document.createElement('div');
      reassurance.className = 'ordering-dog__reassurance';
      reassurance.dataset.orderingReassurance = '';

      if (checkout) {
        checkout.insertAdjacentElement('afterend', reassurance);
      } else {
        totals.insertAdjacentElement('afterend', reassurance);
      }
    }

    // Find the existing switching section on the page.
    const switchingSection = Array.from(
      document.querySelectorAll('h1, h2, h3, h4')
    ).find(el =>
      /switching to pasture\s*&\s*paws\??/i.test(
        (el.textContent || '').trim()
      )
    );

    let switchingHref = '#switching-to-pasture-and-paws';

    if (switchingSection) {
      if (!switchingSection.id) {
        switchingSection.id = 'switching-to-pasture-and-paws';
      }

      switchingHref = `#${switchingSection.id}`;
    }

    // 6.6 Lines under the button, in this order.
    reassurance.innerHTML = `
      <p class="ordering-dog__reassurance-policy ordering-dog__reassurance-policy--strong">
        <strong>Full refund on your first order if your dog doesn't love it.</strong>
      </p>

      <p class="ordering-dog__reassurance-policy">
        Skip, pause or cancel anytime online.
      </p>

      <p class="ordering-dog__reassurance-policy">
        Cooked weekly and delivered fresh, not frozen.
      </p>

      <div class="ordering-dog__summary-help">
        <p>
          New to fresh food?
          <a href="${escapeHTML(switchingHref)}">
            See how to switch gradually.
          </a>
        </p>

        <p>Have a code? Add it at checkout.</p>

        <p>
          Prefer not to order online? Email
          <a href="mailto:support@feedpastureandpaws.com">
            support@feedpastureandpaws.com
          </a>
          with your dog's name, weight and your address, and we'll send
          you a payment link.
        </p>
      </div>
    `;

    updateDeliveryDates();
  }



  /*
   * ============================================================
   * ESCAPE HTML
   * ============================================================
   */

  function escapeHTML(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }


  /*
   * ============================================================
   * MOBILE CHECKOUT BAR
   * ============================================================
   */

  function updateMobileCheckout() {
    const mobileBar =
      $('[data-mobile-checkout]');

    if (!mobileBar) {
      return;
    }

    const shouldShow =
      state.dogs.length > 0 &&
      state.currentStep === 'summary';

    mobileBar.hidden =
      !shouldShow;

    const total =
      calculateOrderTotals(state.dogs).total;

    $$('[data-mobile-total]')
      .forEach(element => {
        element.textContent =
          formatMoney(total);
      });
  }


  /*
   * ============================================================
   * CREATE LINE ITEM PROPERTIES
   * ============================================================
   */

  
  /*
   * Unnamed dogs are numbered in the order they were added:
   * Dog 1, Dog 2 (packing slip only).
   */
  function getUnnamedDogNumber(dog) {
    let count = 0;

    for (const item of state.dogs) {
      if (!String(item.customDogName || '').trim()) count += 1;
      if (item === dog) return count || 1;
    }

    return 1;
  }

  /*
   * 7.2 First delivery, saved on every line item so it shows in the
   * cart, checkout, order, emails and packing slip.
   * The private (_) fields are for Shopify Flow and the packing slip.
   */
  function buildDeliveryProperties() {
    const info = getDeliveryInfo();
    if (!info) return {};

    return {
      'Delivery':
        `${info.dayName}s · First delivery ${info.firstDeliveryText}`,
      '_delivery_route': info.routeKey,
      '_delivery_zip': info.zip,
      '_first_delivery_date': info.firstDeliveryISO
    };
  }

  function buildLineItemProperties(dog) {
    dog = { ...dog, dogNumber: getUnnamedDogNumber(dog) };

    const index = Math.max(1, Number(dog.dogNumber || 1));

    const customName = String(dog.customDogName || '').trim();

    // Packing slip label: the name, or Dog 1, Dog 2 for unnamed dogs.
    const dogName = customName || `Dog ${index}`;

    // Customer-facing label: the name, or "Your dog".
    const customerName = customName || 'Your dog';

    const feedLabel = /full meal|complete meals/i.test(
      String(dog.feed || dog.feedType || '')
    )
      ? 'Complete Meals'
      : 'Topper';

    const ounces = Number(dog.ounces || 0);
    const grams = ouncesToGrams(ounces).toLocaleString('en-US');

    const plan = calculatePlan(
      ounces,
      dog.daysFood || DEFAULT_DAYS_PER_WEEK
    );

    const daysFood = plan.daysPerWeek;
    const containersPerDay = plan.containersPerDay;
    const containersPerWeek = plan.containersPerWeek;

    const containersText =
      pluralize(containersPerWeek, 'container');

    return {
      // Private properties: retain the complete configuration.
      '_Dog name': dogName,
      '_custom_dog_name': customName,
      '_Recipe': String(dog.recipe || ''),
      '_Feed type': feedLabel,
      '_feed_type': String(dog.feedType || ''),
      '_Weight range': String(dog.weight || ''),
      '_Age': String(dog.age || ''),
      '_Activity level': String(dog.activity || ''),
      '_Daily amount': `${ounces} oz (${grams} g) a day`,
      '_Days of food': `${daysFood} days`,
      '_Containers': containersText,
      '_Meals': pluralize(plan.meals, 'meal'),
      '_Daily price': `$${formatMoney(plan.dailyPrice)}`,
      '_Weekly price': `$${formatMoney(plan.weeklyPrice)}`,
      '_Delivery frequency': 'Every week',
      '_Order text':
        `${dog.recipe || RECIPE_NAME}, ${feedLabel}, ` +
        `${ounces} oz (${grams} g) a day, ${daysFood} days of food.`,

      // These three properties are intended for the customer-facing summary.
      'For':
        `${customerName} · ${feedLabel} · ${ounces} oz a day (${grams} g)`,

      // "7 days of food · 14 meals · 7 containers" (item 7.1)
      // 7.1 "7 days of food · 14 meals · 7 containers"
      // Toppers: "4 days of food · 4 containers" (as on their card).
      'Amount':
        feedLabel === 'Topper'
          ? `${pluralize(plan.daysPerWeek, 'day')} of food · ` +
            pluralize(plan.containersPerWeek, 'container')
          : formatPlanAmount(plan),

      // 7.2 "Tuesdays · First delivery Tuesday, Nov 3"
      ...buildDeliveryProperties(),

      // Additional private numeric values for your internal use.
      '_daily_ounces': String(ounces),
      '_days_food': String(daysFood),
      '_meals': String(plan.meals),
      '_containers_per_day': String(containersPerDay),
      '_containers_per_week': String(containersPerWeek),
      '_daily_price': plan.dailyPrice.toFixed(2),
      '_weekly_price': plan.weeklyPrice.toFixed(2)
    };
  }



  /*
   * ============================================================
   * CHECKOUT
   * ============================================================
   */

  async function startWeeklyCheckout() {
    if (state.checkoutInProgress) {
      return;
    }

    if (!state.dogs.length) {
      return;
    }

    /*
     * Every dog must have a valid available variant.
     */

    for (const dog of state.dogs) {
      const validation =
        validateVariant(
          dog.ounces
        );

      if (!validation.valid) {
        alert(validation.message);
        return;
      }
    }

    /*
     * The ordering flow is specifically weekly delivery.
     *
     * Do not accidentally send a one-time purchase.
     */

    if (!weeklySellingPlan) {
      alert(
        'The weekly delivery selling plan is not configured for this product. Please contact support.'
      );

      console.error(
        '[ordering-dog] No weekly selling plan found.',
        sellingPlans
      );

      return;
    }

    state.checkoutInProgress =
      true;

    const buttons = $$(
      '[data-checkout], [data-mobile-checkout-button]'
    );

    buttons.forEach(button => {
      button.disabled = true;
    });

    try {
      const items =
        state.dogs.map(dog => {
          const item = {
            id: Number(dog.variantId),

            // Quantity = days of food a week (1 to 7).
            quantity: clampDays(dog.daysFood),

            selling_plan:
              Number(
                weeklySellingPlan.id
              ),

            properties:
              buildLineItemProperties(dog)
          };

          return item;
        });

      const payload = {
        items
      };

      const cartAddUrl =
        window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root
          ? `${window.Shopify.routes.root}cart/add.js`
          : '/cart/add.js';

      await saveDeliveryToCart();

      const response =
        await fetch(
          cartAddUrl,
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              'Accept':
                'application/json'
            },

            body:
              JSON.stringify(payload)
          }
        );

      const data =
        await response.json();

      if (!response.ok) {
        console.error(
          '[ordering-dog] Cart add failed',
          data
        );

        throw new Error(
          data.description ||
          data.message ||
          'Unable to start checkout.'
        );
      }

      /*
       * Direct checkout.
       */

      window.location.href =
        getCheckoutUrl();

    } catch (error) {
      console.error(
        '[ordering-dog] Checkout error',
        error
      );

      alert(
        error.message ||
        'Something went wrong. Please try again.'
      );

      state.checkoutInProgress =
        false;

      buttons.forEach(button => {
        button.disabled = false;
      });
    }
  }
  
  /*
   * Add all dogs saved in the order summary to the cart.
   * Unlike Start weekly delivery, this does not go directly
   * to checkout.
   */
  async function addSavedDogsToCart(event) {
    if (event) {
      const submitter = event.submitter;

      // Leave other form actions, such as dynamic checkout, alone.
      if (submitter && submitter.name !== 'add') {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
    }

    if (state.checkoutInProgress) return;

    if (!state.dogs.length) {
      alert('Please complete your dog’s feeding plan and review your order before adding it to the cart.');
      showStep(getStartStep());
      return;
    }

    if (!weeklySellingPlan) {
      alert('The weekly delivery selling plan is not configured. Please contact support.');
      console.error('[ordering-dog] Selling plans:', sellingPlans);
      return;
    }

    // Validate every saved dog's selected variant.
    for (const dog of state.dogs) {
      const validation = validateVariant(dog.ounces);

      if (!validation.valid) {
        alert(validation.message);
        return;
      }

      if (!dog.variantId) {
        alert(`The amount for ${dog.customDogName || 'your dog'} could not be found. Please edit the feeding plan.`);
        return;
      }
    }

    state.checkoutInProgress = true;

    const addButton = event?.submitter;
    if (addButton) addButton.disabled = true;

    try {
      const items = state.dogs.map(dog => ({
        id: Number(dog.variantId),
        quantity: clampDays(dog.daysFood), // days a week
        selling_plan: Number(weeklySellingPlan.id),
        properties: buildLineItemProperties(dog)
      }));

      const cartAddUrl =
        window.Shopify?.routes?.root
          ? `${window.Shopify.routes.root}cart/add.js`
          : '/cart/add.js';

      await saveDeliveryToCart();

      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('[ordering-dog] Add to cart failed:', data);
        throw new Error(
          data.description ||
          data.message ||
          'Unable to add the dogs to your cart.'
        );
      }

      // Show the cart, not checkout.
      const cartUrl =
        window.Shopify?.routes?.root
          ? `${window.Shopify.routes.root}cart`
          : '/cart';

      window.location.href = cartUrl;
    } catch (error) {
      console.error('[ordering-dog] Add to cart error:', error);
      alert(error.message || 'Something went wrong. Please try again.');

      state.checkoutInProgress = false;
      if (addButton) addButton.disabled = false;
    }
  }

  /*
   * ============================================================
   * START NEW DOG
   * ============================================================
   */

  function startNewDog() {
    state.dogName = '';
    state.feed = '';
    state.weight = '';
    state.age = '';
    state.activity = '';

    state.ounces =
      DEFAULT_AMOUNT;

    state.knownAmountPath = false;

    state.customAmount = false;

    state.daysPerWeek =
      DEFAULT_DAYS_PER_WEEK;

    setSelected(
      '[data-days-per-week]',
      'data-days-per-week',
      String(DEFAULT_DAYS_PER_WEEK)
    );

    state.largeAmountConfirmed =
      false;

    state.editingDogIndex =
      null;

    state.stepHistory = [];

    const nameInput =
      $('[data-dog-name]');

    if (nameInput) {
      nameInput.value = '';
    }

    setSelected(
      '[data-feed-choice]',
      'data-feed-choice',
      ''
    );

    setSelected(
      '[data-weight]',
      'data-weight',
      ''
    );

    setSelected(
      '[data-age]',
      'data-age',
      ''
    );

    setSelected(
      '[data-activity]',
      'data-activity',
      ''
    );

    setSelected(
      '[data-topper-amount]',
      'data-topper-amount',
      ''
    );

    updateAmountUI();
    updatePuppyMessage();
    updateDogNameOutputs();

    showStep(
      'feed',
      {
        pushHistory: false
      }
    );
  }


  /*
   * ============================================================
   * EVENT HANDLERS
   * ============================================================
   */

  /*
   * Feed type.
   */

  $$('[data-feed-choice]').forEach(
    button => {
      button.addEventListener(
        'click',
        () => {
          state.feed =
            button.dataset.feedChoice;

          state.knownAmountPath = false;

          setSelected(
            '[data-feed-choice]',
            'data-feed-choice',
            state.feed
          );

          state.stepHistory = [];

          if (
            state.feed ===
            'Complete Meals'
          ) {
            showStep(
              'weight',
              {
                pushHistory: true
              }
            );
          } else {
            showStep(
              'topper',
              {
                pushHistory: true
              }
            );
          }
        }
      );
    }
  );


  /*
   * Dog name.
   */

  const dogNameInput =
    $('[data-dog-name]');

  if (dogNameInput) {
    dogNameInput.addEventListener('input', event => {
      state.dogName = event.target.value;
      updateDogNameOutputs();
    });

    dogNameInput.addEventListener('blur', () => {
      const name = String(dogNameInput.value || '').trim();

      state.dogName = name
        ? name.charAt(0).toLocaleUpperCase() + name.slice(1)
        : '';

      dogNameInput.value = state.dogName;
      updateDogNameOutputs();
    });
  }


  /*
   * Weight.
   */

  $$('[data-weight]').forEach(
    button => {
      button.addEventListener(
        'click',
        () => {
          state.weight =
            button.dataset.weight;

          state.knownAmountPath = false;

          setSelected(
            '[data-weight]',
            'data-weight',
            state.weight
          );

          showStep(
            'age'
          );
        }
      );
    }
  );


  /*
   * Already know amount.
   */

  const knownAmountButton = $('[data-known-amount]');

  if (knownAmountButton) {
    knownAmountButton.addEventListener('click', () => {
      state.knownAmountPath = true;
      amountUnit = 'oz';
      setAmount(DEFAULT_AMOUNT, 'oz');
      showStep('amount');
    });
  }


  /*
   * Age.
   */

  $$('[data-age]').forEach(
    button => {
      button.addEventListener(
        'click',
        () => {
          state.age =
            button.dataset.age;

          setSelected(
            '[data-age]',
            'data-age',
            state.age
          );

          updatePuppyMessage();

          /*
           * Puppy cannot continue.
           */

          if (
            state.age ===
            'Puppy'
          ) {
            return;
          }

          showStep(
            'activity'
          );
        }
      );
    }
  );


  /*
   * Activity.
   */

  $$('[data-activity]').forEach(
    button => {
      button.addEventListener(
        'click',
        () => {
          state.activity =
            button.dataset.activity;

          setSelected(
            '[data-activity]',
            'data-activity',
            state.activity
          );

          const amount =
            getLookupAmount(
              state.weight,
              state.age,
              state.activity
            );

          if (
            amount === null
          ) {
            alert(
              'We could not find a feeding amount for this combination. Please contact support.'
            );

            return;
          }

          setAmount(amount);

          state.customAmount = false;

          showStep(
            'result'
          );
        }
      );
    }
  );


  /*
   * Amount input.
   */  
  /*
  * Shared amount editor: unit switch.
  */
  $$('[data-amount-unit]').forEach(button => {
    button.addEventListener('click', () => {
      const requestedUnit = button.dataset.amountUnit;

      if (requestedUnit !== 'oz' && requestedUnit !== 'grams') {
        return;
      }

      amountUnit = requestedUnit;
      updateAmountUI();
    });
  });

  /*
  * Manual amount entry.
  */
  const amountInput = $('[data-amount-input]');

  if (amountInput) {
    amountInput.addEventListener('input', event => {
      const raw = event.target.value;

      if (raw.trim() === '') {
        return;
      }

      state.ounces = normalizeAmount(raw, amountUnit);
      state.largeAmountConfirmed = state.ounces <= 100;

      // Keep the user's typed value visible while updating price and grams.
      updateAmountUI({ preserveInput: true });
    });

    amountInput.addEventListener('blur', () => {
      setAmount(amountInput.value, amountUnit);
    });
  }

  /*
  * Minus: 2 oz or 50 g.
  */
  const amountMinus = $('[data-amount-minus]');

  if (amountMinus) {
    amountMinus.addEventListener('click', () => {
      if (amountUnit === 'grams') {
        const currentGrams = ouncesToGrams(state.ounces);
        setAmount(Math.max(
          MIN_OUNCES * 28.349523125,
          currentGrams - 50
        ), 'grams');
      } else {
        setAmount(state.ounces - AMOUNT_STEP, 'oz');
      }
    });
  }

  /*
  * Plus: 2 oz or 50 g.
  */
  const amountPlus = $('[data-amount-plus]');

  if (amountPlus) {
    amountPlus.addEventListener('click', () => {
      if (amountUnit === 'grams') {
        const currentGrams = ouncesToGrams(state.ounces);
        setAmount(Math.min(
          MAX_OUNCES * 28.349523125,
          currentGrams + 50
        ), 'grams');
      } else {
        setAmount(state.ounces + AMOUNT_STEP, 'oz');
      }
    });
  }



  /*
   * Amount Continue.
   */

  const amountContinue = $('[data-amount-continue]');

  if (amountContinue) {
    amountContinue.addEventListener('click', () => {
      const input = $('[data-amount-input]');

      if (input && input.value.trim() !== '') {
        setAmount(input.value, amountUnit);
      }

      if (
        state.ounces > 100 &&
        !state.largeAmountConfirmed
      ) {
        const confirmation = $('[data-large-amount]');
        if (confirmation) confirmation.hidden = false;
        return;
      }

      const validation = validateVariant(state.ounces);

      if (!validation.valid) {
        alert(validation.message);
        return;
      }

      state.customAmount = true;
      showStep('result');
    });
  }


  /*
   * Large amount: yes.
   */

  const largeAmountYes = $('[data-large-amount-yes]');

  if (largeAmountYes) {
    largeAmountYes.addEventListener('click', () => {
      state.largeAmountConfirmed = true;

      const validation = validateVariant(state.ounces);

      if (!validation.valid) {
        alert(validation.message);
        return;
      }

      state.customAmount = true;
      showStep('result');
    });
  }


  /*
   * Large amount: change.
   */

  const largeAmountChange = $('[data-large-amount-change]');

  if (largeAmountChange) {
    largeAmountChange.addEventListener('click', () => {
      state.largeAmountConfirmed = false;

      const confirmation = $('[data-large-amount]');
      if (confirmation) confirmation.hidden = true;

      const input = $('[data-amount-input]');

      if (input) {
        input.focus();
        input.select();
      }
    });
  }


  /*
   * Change amount from result.
   */

  const changeAmount = $('[data-change-amount]');

  if (changeAmount) {
    changeAmount.addEventListener('click', () => {
      // Keep the recommended/current amount when opening this editor.
      amountUnit = 'oz';
      updateAmountUI();
      showStep('amount');
    });
  }


  /*
   * Topper.
   */

  $$('[data-topper-amount]').forEach(
    button => {
      button.addEventListener(
        'click',
        () => {
          const amount =
            Number(
              button.dataset.topperAmount
            );

          if (
            !TOPPER_OPTIONS.includes(
              amount
            )
          ) {
            return;
          }

          state.ounces =
            amount;

          state.customAmount = false;

          setSelected(
            '[data-topper-amount]',
            'data-topper-amount',
            String(amount)
          );

          const validation =
            validateVariant(amount);

          if (!validation.valid) {
            alert(
              validation.message
            );

            return;
          }

          showStep(
            'result'
          );
        }
      );
    }
  );


  /*
   * Continue from result / review.
   */

  const reviewPlan =
    $('[data-review-plan]');

  if (reviewPlan) {
    reviewPlan.addEventListener(
      'click',
      () => {
        const validation =
          validateVariant(
            state.ounces
          );

        if (!validation.valid) {
          alert(
            validation.message
          );

          return;
        }

        saveCurrentDog();

        renderSummary();

        showStep(
          'summary',
          {
            pushHistory: true
          }
        );
      }
    );
  }


  /*
   * Back buttons.
   */

  $$('[data-back]').forEach(
    button => {
      button.addEventListener(
        'click',
        event => {
          event.preventDefault();

          goBack();
        }
      );
    }
  );


  /*
   * Add another dog.
   */

  const addDog =
    $('[data-add-dog]');

  if (addDog) {
    addDog.addEventListener(
      'click',
      () => {
        startNewDog();
      }
    );
  }


  /*
   * Dynamic Edit / Remove handlers.
   */
  root.addEventListener(
    'click',
    event => {
      const editButton =
        event.target.closest(
          '[data-edit-dog]'
        );

      if (editButton) {
        const index =
          Number(
            editButton.dataset.editDog
          );

        const dog =
          state.dogs[index];

        if (dog) {
          loadDogIntoState(
            dog,
            index
          );
        }

        return;
      }

      const removeButton =
        event.target.closest(
          '[data-remove-dog]'
        );

      if (removeButton) {
        const index =
          Number(
            removeButton.dataset.removeDog
          );

        removeDog(index);

        return;
      }
    }
  );


  /*
   * Checkout.
   */

  $$('[data-checkout]').forEach(
    button => {
      button.addEventListener(
        'click',
        startWeeklyCheckout
      );
    }
  );

  const mobileCheckoutButton =
    $('[data-mobile-checkout-button]');

  if (mobileCheckoutButton) {
    mobileCheckoutButton.addEventListener(
      'click',
      startWeeklyCheckout
    );
  }
  /*
   * Normal Shopify Add to cart form.
   * Adds all dogs saved in the order summary.
   */
  const standardProductForm = document.querySelector(
    'form[data-type="add-to-cart-form"]'
  );

  if (standardProductForm) {
    standardProductForm.addEventListener(
      'submit',
      addSavedDogsToCart,
      true
    );
  }

  /*
   * ============================================================
   * INITIALIZATION
   * ============================================================
   */

  function initialize() {
    

    updateDogNameOutputs();
    updatePuppyMessage();
    const amountInput =
    $('[data-amount-input]');

    if (amountInput) {
      amountInput.min = String(MIN_OUNCES);
      amountInput.max = String(MAX_OUNCES);
      amountInput.step = String(AMOUNT_STEP);
      amountInput.inputMode = 'numeric';
    }
    setAmount(
      DEFAULT_AMOUNT
    );

    updateDeliveryDates();

    /*
     * Days per week buttons (any element with
     * data-days-per-week="1".."7").
     */
    root.addEventListener('click', event => {
      const button = event.target.closest('[data-days-per-week]');

      if (!button || !root.contains(button)) return;

      event.preventDefault();
      setDaysPerWeek(button.dataset.daysPerWeek);
    });

    setDaysPerWeek(DEFAULT_DAYS_PER_WEEK);

    checkVariantPrices();

    bindZipStep();

    // 3.3 Remember the ZIP for the visit.
    const rememberedZip = getRememberedZip();
    const rememberedRoute = getRouteForZip(rememberedZip);

    if (rememberedRoute) {
      state.zip = rememberedZip;
      state.route = rememberedRoute;

      const zipInput = $('[data-zip-input]');
      if (zipInput) zipInput.value = rememberedZip;
    }

    // 3.1 Banner and scroll only when the ad link has ?route=
    const fromAd = showRouteBanner();

    // 3.4 / 4.6 Back from a waitlist sign-up: show the thanks.
    const returnedFromWaitlist = restoreWaitlistAfterReload();

    showStep(
      getStartStep(),
      {
        pushHistory: false,
        scroll: fromAd || Boolean(returnedFromWaitlist)
      }
    );

    // 4.6 Back from the puppy sign-up: show the age question again.
    if (returnedFromWaitlist === 'puppy' && state.route) {
      showStep('age', { pushHistory: false });
    }

    /*
     * Helpful developer diagnostics.
     */
    root.orderingDogState =
      () => ({
        ...state,
        variantCount:
          variantByDailyAmount.size,
        weeklySellingPlan,
        variants
      });

    console.info(
      '[ordering-dog] initialized',
      {
        variants:
          variantByDailyAmount.size,

        weeklySellingPlan,

        expectedVariants:
          Math.floor(
            (MAX_OUNCES - MIN_OUNCES) /
            AMOUNT_STEP
          ) + 1
      }
    );
  }

  initialize();

})();